/** Стили точек и линий на картах. */

import Circle from "ol/style/Circle";
import Fill from "ol/style/Fill";
import RegularShape from "ol/style/RegularShape";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import Text from "ol/style/Text";

/**
 * Значение токена из tokens.css.
 *
 * OpenLayers принимает только готовые строки цвета, var() он не понимает.
 * Дублировать значения в двух местах нельзя, поэтому читаем их из стилей.
 * Запасной цвет нужен на случай, если модуль исполнится до применения CSS.
 */
function token(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === "" ? fallback : value;
}

/**
 * Цель — тёплая, ответ игрока — холодный.
 *
 * Это не украшение, а кодировка: на карте результата две точки, и по цвету
 * должно быть сразу понятно, где правда, а где догадка. Цель совпадает с
 * акцентом интерфейса, потому что именно к ней всё время ведут очки.
 */
export const COLOR_TARGET = token("--accent", "#ffab2e");
export const COLOR_GUESS = token("--marker-guess", "#56c7f0");

const OUTLINE = token("--bg", "#070d0c");

/**
 * Тот же цвет, но прозрачный.
 *
 * Токены заданы в hex, а заливка страны должна просвечивать карту под собой.
 * color-mix здесь не годится: OpenLayers принимает только готовые строки.
 */
function withAlpha(color: string, alpha: number): string {
  const hex = color.replace("#", "");
  const value = Number.parseInt(hex.length === 3 ? hex.replace(/./g, "$&$&") : hex, 16);

  return `rgba(${String((value >> 16) & 255)}, ${String((value >> 8) & 255)}, ${String(value & 255)}, ${String(alpha)})`;
}

function marker(color: string): Style {
  return new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: OUTLINE, width: 3 }),
    }),
  });
}

export const STYLE_GUESS = marker(COLOR_GUESS);
export const STYLE_TARGET = marker(COLOR_TARGET);

/**
 * Цель на карте разбора с номером раунда внутри.
 *
 * На карте партии таких точек пять или десять, и без номера непонятно, какая
 * из них к какой строке списка относится. Стили складываются по номеру один
 * раз: OpenLayers спрашивает стиль на каждую перерисовку.
 */
const numbered = new Map<number, Style>();

export function styleNumberedTarget(index: number): Style {
  const cached = numbered.get(index);
  if (cached !== undefined) return cached;

  const style = new Style({
    image: new Circle({
      radius: 12,
      fill: new Fill({ color: COLOR_TARGET }),
      stroke: new Stroke({ color: OUTLINE, width: 3 }),
    }),
    text: new Text({
      text: String(index),
      font: `700 11px ${token("--font", "sans-serif")}`,
      fill: new Fill({ color: OUTLINE }),
      offsetY: 1,
    }),
  });

  numbered.set(index, style);
  return style;
}

/**
 * Страны на карте догадки.
 *
 * В режиме стран игрок выбирает не точку, а страну, поэтому границы должны
 * быть видны, но не спорить с картой: обычная страна — тонкий контур без
 * заливки, под курсором она подсвечивается, выбранная заливается тем же
 * холодным цветом, каким в обычном раунде отмечена догадка. Кодировка одна:
 * холодное — это ответ игрока.
 */
export const STYLE_COUNTRY = new Style({
  // Не цветом линий интерфейса: они рассчитаны на поверхности карточек, а
  // здесь под ними карта, и на ней такой контур не виден вовсе
  stroke: new Stroke({ color: withAlpha(token("--text-faint", "#849d96"), 0.55), width: 1 }),
  // Заливка прозрачная, но она нужна: без неё попадание считается только по
  // самой линии границы, и ткнуть в середину страны становится нельзя
  fill: new Fill({ color: "rgba(0, 0, 0, 0)" }),
});

export const STYLE_COUNTRY_HOVER = new Style({
  stroke: new Stroke({ color: COLOR_GUESS, width: 1.5 }),
  fill: new Fill({ color: withAlpha(COLOR_GUESS, 0.18) }),
});

export const STYLE_COUNTRY_PICKED = new Style({
  stroke: new Stroke({ color: COLOR_GUESS, width: 2 }),
  fill: new Fill({ color: withAlpha(COLOR_GUESS, 0.42) }),
});

/** Линия промаха между догадкой и целью. */
export const STYLE_LINE = new Style({
  stroke: new Stroke({ color: token("--text-dim", "#9fb3ad"), width: 2, lineDash: [6, 6] }),
});

/**
 * Перекрестие в центре показанного участка.
 *
 * Очки считаются от этой точки, поэтому игрок должен видеть, куда целиться, —
 * на снегу, на тёмной воде, на пёстрой застройке и на любой яркости экрана.
 * Отсюда три слоя: широкая тёмная подложка, белые линии поверх неё и янтарное
 * кольцо тем же цветом, каким цель отмечена на карте результата. Один белый
 * штрих в два пикселя, как было раньше, на светлом снимке попросту теряется.
 */
const RETICLE_RADIUS = 22;

export const STYLE_CENTER: Style[] = [
  new Style({
    image: new RegularShape({
      points: 4,
      radius: RETICLE_RADIUS,
      radius2: 0,
      stroke: new Stroke({ color: "rgba(0, 0, 0, 0.65)", width: 8 }),
    }),
  }),
  new Style({
    image: new RegularShape({
      points: 4,
      radius: RETICLE_RADIUS,
      radius2: 0,
      stroke: new Stroke({ color: "rgba(255, 255, 255, 0.98)", width: 3 }),
    }),
  }),
  // Кольцо вокруг самой точки: тёмный контур снаружи, янтарь внутри. Центр
  // остаётся открытым — под ним должно быть видно, что именно ищем
  new Style({
    image: new Circle({
      radius: 8,
      stroke: new Stroke({ color: "rgba(0, 0, 0, 0.65)", width: 6 }),
    }),
  }),
  new Style({
    image: new Circle({
      radius: 8,
      stroke: new Stroke({ color: COLOR_TARGET, width: 3 }),
    }),
  }),
];
